import type { CostBasis } from '../../types/costBasis'
import type {
  FeeSubCategory,
  IncomeSubCategory,
  TradeSubCategory,
} from '../../types/transaction'
import { createBlankTransaction } from './createBlankTransaction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'

type CreateTransactionsByCrossTradeActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly use5PercentRule?: boolean
    readonly feeSubCategory?: FeeSubCategory
    readonly incomeSubCategory?: IncomeSubCategory
    readonly tradeSubCategory?: TradeSubCategory
  }

type CreateTransactionsByCrossTradeActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByCrossTradeAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  isLocked = false,
  use5PercentRule = false,
  feeSubCategory,
  incomeSubCategory,
  tradeSubCategory,
}: CreateTransactionsByCrossTradeActionParameters): CreateTransactionsByCrossTradeActionReturnTypes => {
  if (action.crossId === undefined) {
    action.crossId = ''
  }
  if (action.crossType === undefined) {
    action.crossType = 'middle'
  }

  // handle cross action bundle
  const crossActionBundle = crossActionBundles.get(action.crossId) ?? {
    id: action.crossId,
    actions: [],
  }
  crossActionBundle.actions.push(action)
  const actionTypes = new Set(
    crossActionBundle.actions.map((action) => action.crossType),
  )
  if (!actionTypes.has('start') || !actionTypes.has('end')) {
    crossActionBundles.set(action.crossId, crossActionBundle)

    if (isLocked) {
      return {
        transactions: [],
        loans,
        crossActionBundles,
        assetBalances,
        assetBorrows,
        usedBalances,
      }
    }

    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances,
      assetBorrows,
      usedBalances,
    })
    return {
      transactions: [result.transaction],
      loans,
      crossActionBundles,
      assetBalances: result.assetBalances,
      assetBorrows: result.assetBorrows,
      usedBalances: result.usedBalances,
    }
  }
  const transfers = crossActionBundle.actions.flatMap(
    (action) => action.transfers,
  )
  crossActionBundles.delete(action.crossId)

  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  return createTransactionsByTradeAction({
    actionId,
    action: {
      ...action,
      transfers,
    },
    loans,
    crossActionBundles,
    assetBalances,
    assetBorrows,
    usedBalances,
    nftAcquiredTimestamps,
    costBasis,
    isLocked,
    use5PercentRule,
    feeSubCategory,
    incomeSubCategory,
    tradeSubCategory,
  })
}
